import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Wallet,
  TrendingUp,
  ShoppingCart,
  FileText,
  Shield,
  ArrowRight,
  Check,
  Star,
  ChevronRight,
  Zap,
  PiggyBank,
  LineChart
} from 'lucide-react';

const features = [
  {
    icon: Wallet,
    title: 'Slimme Huishoudboekhouding',
    description: 'Importeer je transacties via CSV, bekijk je uitgaven per categorie en vergelijk je uitgaven met richtbedragen van het Nibud. Automatische bankkoppeling volgt later.',
    status: 'Nu beschikbaar',
    color: 'bg-primary/10 text-primary',
  },
  {
    icon: ShoppingCart,
    title: 'Boodschappen Vergelijker',
    description: 'Werk met weekplanning, bon-upload en prijsobservaties. Winkelvergelijking wordt stap voor stap uitgebreid.',
    status: 'Nu beschikbaar',
    color: 'bg-secondary/20 text-secondary-foreground',
  },
  {
    icon: FileText,
    title: 'Contract Bewaking',
    description: 'Bewaar contracten, betaaldatums en bijlagen op een plek. De energievergelijker is al te gebruiken, maar nog in opbouw.',
    status: 'In opbouw',
    color: 'bg-blue-100 text-blue-700',
  },
  {
    icon: LineChart,
    title: 'Scenario Simulator',
    description: 'Bekijk wat er gebeurt als je inkomen verandert of je vaste lasten stijgen.',
    status: 'Nu beschikbaar',
    color: 'bg-purple-100 text-purple-700',
  },
  {
    icon: PiggyBank,
    title: 'Spaardoelen',
    description: 'Maak spaardoelen aan en volg stap voor stap hoe ver je bent.',
    status: 'Nu beschikbaar',
    color: 'bg-green-100 text-green-700',
  },
  {
    icon: Shield,
    title: 'Veilig & Privacy',
    description: 'We gaan zorgvuldig om met je gegevens. Extra beveiliging en meer instellingen worden later toegevoegd.',
    status: 'Binnenkort uitgebreider',
    color: 'bg-gray-100 text-gray-700',
  }
];

const statusCards = [
  {
    name: 'Nu live',
    role: 'Vandaag bruikbaar',
    text: 'Transacties importeren, contracten beheren, boodschappen plannen, spaardoelen volgen en gezondheidsoverzicht bekijken.',
  },
  {
    name: 'In opbouw',
    role: 'Beperkt beschikbaar',
    text: 'De energievergelijker is al te gebruiken. De uitkomsten zijn nog in ontwikkeling en kunnen nog veranderen.',
  },
  {
    name: 'Binnenkort beschikbaar',
    role: 'In ontwikkeling',
    text: 'Een demo, automatische bankkoppeling, meer prijsvergelijking en extra hulp bij terugkerende taken worden nog verder uitgewerkt.',
  }
];

const legalLinks = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/voorwaarden', label: 'Voorwaarden' },
  { to: '/contact', label: 'Contact' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [navigate, user]);

  if (user) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Wallet className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-xl">budgettoppers</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                onClick={() => navigate('/auth')}
                className="hidden sm:inline-flex"
              >
                Inloggen
              </Button>
              <Button
                data-testid="nav-register-btn"
                onClick={() => navigate('/auth?mode=register')}
                className="rounded-full bg-primary hover:bg-primary/90"
              >
                Gratis starten
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-20 md:pt-40 md:pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-secondary-foreground text-sm font-medium">
                <Zap className="w-4 h-4" />
                <span>Praktische geldplanner voor huishoudens</span>
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
                Van financiële stress naar{' '}
                <span className="text-primary">gemoedsrust</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                Breng je geldzaken, contracten, boodschappen en spaardoelen samen in een overzichtelijke app voor thuis.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-cta-btn"
                  size="lg"
                  onClick={() => navigate('/auth?mode=register')}
                  className="rounded-full bg-primary hover:bg-primary/90 text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                >
                  Start gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full border-2 text-lg px-8 py-6"
                  disabled
                >
                  Demo binnenkort beschikbaar
                </Button>
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-muted border-2 border-background"
                    />
                  ))}
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Eerlijk over wat al werkt</p>
                  <p className="text-sm text-muted-foreground">Onderdelen die nog groeien zijn duidelijk aangegeven</p>
                </div>
              </div>
            </div>
            <div className="relative hidden md:block">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-3xl" />
              <div className="relative bg-white rounded-2xl shadow-card-hover p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-heading font-semibold">Maandoverzicht</span>
                  <span className="text-sm text-muted-foreground">Januari 2026</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-green-50">
                    <p className="text-sm text-muted-foreground">Inkomsten</p>
                    <p className="font-mono text-2xl font-bold text-green-600">€3.450</p>
                  </div>
                  <div className="p-4 rounded-xl bg-red-50">
                    <p className="text-sm text-muted-foreground">Uitgaven</p>
                    <p className="font-mono text-2xl font-bold text-red-600">€2.180</p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-secondary/10 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-secondary-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold">€284 besparing mogelijk</p>
                    <p className="text-sm text-muted-foreground">3 contracten lopen af</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Alles wat je nodig hebt
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Handige onderdelen voor budget, contracten, boodschappen, gezondheid en planning, met eerlijke labels als iets nog in opbouw is.
            </p>
          </div>
          <div className="mb-10 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 md:flex md:items-start md:justify-between md:gap-6">
            <div>
              <p className="font-semibold">Automatische bankkoppeling komt later</p>
              <p className="mt-1 text-amber-800">
                Je kunt nu al goed starten met CSV-import. Daarmee krijg je transacties, categorieen en budgetsignalen meteen werkend in de app.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <Button
                variant="outline"
                className="rounded-full border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start met CSV-import
              </Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="feature-card p-6 shadow-card hover:shadow-card-hover transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-4`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <div className="mb-3 inline-flex rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                  {feature.status}
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Productstatus
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {statusCards.map((card, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-2xl shadow-card"
              >
                <div className="flex items-center gap-2 mb-4 text-primary">
                  <Check className="w-5 h-5" />
                  <p className="font-semibold">{card.name}</p>
                </div>
                <p className="text-foreground mb-4">{card.text}</p>
                <div>
                  <p className="text-sm text-muted-foreground">{card.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Zo start je vandaag al
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Ook zonder automatische bankkoppeling kun je al snel overzicht krijgen met een eenvoudig transactiebestand van je bank.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                step: '1',
                title: 'Download je transacties bij je bank',
                text: 'Log in bij je bank en kies een export van je transacties, meestal als CSV-bestand.',
              },
              {
                step: '2',
                title: 'Upload het bestand in budgettoppers',
                text: 'Ga in de app naar Transacties en kies daar je CSV-bestand. Je hoeft geen technische kennis te hebben.',
              },
              {
                step: '3',
                title: 'Controleer direct je eerste inzicht',
                text: 'Na de import zie je categorieen, totalen, budgetsignalen en je maandbeeld terug op het dashboard.',
              },
            ].map((item) => (
              <div key={item.step} className="rounded-2xl border bg-muted/20 p-6 shadow-card">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-muted-foreground">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Simpele prijzen
            </h2>
            <p className="text-lg text-muted-foreground">Begin gratis en kies later wat bij je past</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="p-8 rounded-2xl border-2 border-border bg-white">
              <h3 className="font-heading font-semibold text-xl mb-2">Gratis</h3>
              <p className="text-muted-foreground mb-6">Perfect om te beginnen</p>
              <div className="mb-6">
                <span className="text-4xl font-bold font-heading">€0</span>
                <span className="text-muted-foreground">/maand</span>
              </div>
              <ul className="space-y-3 mb-8">
                {['1 rekening registreren', 'Transacties importeren en categoriseren', 'Contractoverzicht en herinneringen', 'Nibud vergelijking'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-free-btn"
                variant="outline"
                className="w-full rounded-full"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start gratis
              </Button>
            </div>

            {/* Premium Plan */}
            <div className="p-8 rounded-2xl border-2 border-primary bg-primary/5 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  Populair
                </span>
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2">Premium</h3>
              <p className="text-muted-foreground mb-6">Voor wie meer overzicht en extra hulp wil</p>
              <div className="mb-6">
                <span className="text-4xl font-bold font-heading">€3,99</span>
                <span className="text-muted-foreground">/maand</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  'Meerdere rekeningen registreren',
                  'Boodschappenplanning en prijsobservaties',
                  'Energievergelijker in opbouw',
                  'Internetvergelijking via doorverwijzing',
                  'Contractbijlagen opslaan',
                  'Extra overzichten en vooruitkijken'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid="pricing-premium-btn"
                className="w-full rounded-full bg-primary hover:bg-primary/90"
                onClick={() => navigate('/auth?mode=register')}
              >
                Start Premium proefperiode
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + Footer */}
      <section className="bg-foreground pt-14 text-white md:pt-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="rounded-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-900 px-8 py-12 shadow-[0_30px_80px_-30px_rgba(12,32,23,0.65)] md:px-12 md:py-14">
            <div className="grid gap-8 lg:grid-cols-[1.4fr_auto] lg:items-center">
              <div>
                <p className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-white/65">
                  Financiele rust begint hier
                </p>
                <h2 className="font-heading text-3xl font-bold leading-tight text-white md:text-5xl">
                  Klaar om financiële rust te ervaren?
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-8 text-white/80 md:text-xl">
                  Begin eenvoudig met meer overzicht in je geld, contracten en dagelijkse keuzes.
                </p>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row lg:flex-col">
                <Button
                  data-testid="cta-btn"
                  size="lg"
                  onClick={() => navigate('/auth?mode=register')}
                  className="rounded-full bg-white px-8 py-6 text-lg text-primary hover:bg-white/90"
                >
                  Gratis beginnen
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/auth')}
                  className="rounded-full border-white/25 bg-white/5 px-8 py-6 text-lg text-white hover:bg-white/10"
                >
                  Inloggen
                </Button>
              </div>
            </div>
          </div>
        </div>

        <footer className="pb-12 pt-10 text-white/80 md:pb-16 md:pt-14">
          <div className="mx-auto max-w-7xl px-6">
            <div className="grid gap-10 border-t border-white/10 pt-10 lg:grid-cols-[1.2fr_0.8fr] lg:gap-14">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/8">
                    <Wallet className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <span className="font-heading text-xl font-bold text-white">budgettoppers</span>
                    <p className="text-sm text-white/55">Een dienst van Buybeez</p>
                  </div>
                </div>

                <p className="mt-5 max-w-xl text-sm leading-7 text-white/70">
                  budgettoppers helpt huishoudens en gezinnen overzicht te krijgen in geld, contracten en dagelijkse
                  keuzes, met een duidelijke focus op rust, inzicht en een eerlijke weergave van wat je vandaag al kunt gebruiken.
                </p>

                <div className="flex flex-wrap gap-3 text-sm">
                  <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2">KvK 92484476</span>
                  <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2">BTW NL004956879B14</span>
                  <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2">D-U-N-S 494977417</span>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/50">Bedrijfsgegevens</p>
                  <div className="mt-4 space-y-3 text-sm leading-7 text-white/75">
                    <p>Buybeez</p>
                    <p>Nederland</p>
                    <p>
                      <a href="mailto:budgettoppers@buybeez.nl" className="transition-colors hover:text-white">
                        budgettoppers@buybeez.nl
                      </a>
                    </p>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-6">
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/50">Juridisch</p>
                  <div className="mt-4 flex flex-col gap-3 text-sm sm:gap-4">
                    {legalLinks.map((link) => (
                      <Link key={link.to} to={link.to} className="transition-colors hover:text-white">
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-sm text-white/55 sm:flex-row sm:items-center sm:justify-between">
              <p>© 2026 budgettoppers. Alle rechten voorbehouden.</p>
              <p>Gemaakt voor rust, inzicht en eerlijke keuzes.</p>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
}
